import { useEffect, useState } from "react";
import { countdownTeile, zweistellig, type CountdownTeile } from "@/lib/termine/infoLeiste";

// Countdown bis zum Beginn einer Infoveranstaltung (Daniel 22.10.2026: „Wir müssen in der Leiste oben einen Countdown
// haben und auch auf der Landingpage“). Zwei Fassungen: schmal für die Hinweisleiste (Tailwind, alte und neue Webseite)
// und als Kacheln für Landingpage und Pop-up (Klassen `iv-countdown` in `webseite.css`, nur unter `.mk-webseite`). Die
// Zahlen ticken jede Sekunde und sind für Vorleseprogramme ausgeblendet; vorgelesen wird ein ruhiger Satz.

/** Die aktuelle Zeit, jede Sekunde neu (nur solange `aktiv`). */
export function useSekundentakt(aktiv = true): number {
  const [jetzt, setJetzt] = useState(() => Date.now());
  useEffect(() => {
    if (!aktiv) return;
    const takt = window.setInterval(() => setJetzt(Date.now()), 1000);
    return () => window.clearInterval(takt);
  }, [aktiv]);
  return jetzt;
}

const EINHEITEN: { schluessel: keyof CountdownTeile; eins: string; viele: string; kurz: string; lang: string }[] = [
  { schluessel: "tage", eins: "Tag", viele: "Tage", kurz: "T", lang: "Tage" },
  { schluessel: "stunden", eins: "Std", viele: "Std", kurz: "Std", lang: "Stunden" },
  { schluessel: "minuten", eins: "Min", viele: "Min", kurz: "Min", lang: "Minuten" },
  { schluessel: "sekunden", eins: "Sek", viele: "Sek", kurz: "Sek", lang: "Sekunden" },
];

/** Ruhiger Satz für Vorleseprogramme: „Noch 10 Tage und 23 Stunden.“ */
function vorlesen(t: CountdownTeile): string {
  if (t.tage > 0) return `Noch ${t.tage} ${t.tage === 1 ? "Tag" : "Tage"} und ${t.stunden} Stunden.`;
  if (t.stunden > 0) return `Noch ${t.stunden} Stunden und ${t.minuten} Minuten.`;
  return `Noch ${t.minuten} Minuten.`;
}

/**
 * Schmal für die Hinweisleiste: vier kleine Kacheln auf Orange, am Handy enger und unter 380 px ohne Sekunden (sonst
 * passt der Knopf „Kostenlos anmelden“ nicht daneben). Nach dem Beginn „Jetzt live“.
 */
export function CountdownLeiste({ beginn }: { beginn: string }) {
  const jetzt = useSekundentakt();
  const teile = countdownTeile(beginn, jetzt);
  if (!teile) return <span className="shrink-0 text-[13px] font-extrabold">Jetzt live</span>;
  return (
    <span className="flex shrink-0 items-center gap-0.5 sm:gap-1">
      <span className="sr-only">{vorlesen(teile)}</span>
      {EINHEITEN.map((e) => (
        <span
          key={e.schluessel}
          aria-hidden="true"
          className={`flex items-baseline gap-0.5 rounded-md bg-white/60 px-1 py-1 leading-none sm:px-1.5 ${
            e.schluessel === "sekunden" ? "max-[379px]:hidden" : ""
          }`}
        >
          <b className="text-[14px] font-extrabold tabular-nums sm:text-[15px]">
            {e.schluessel === "tage" ? teile.tage : zweistellig(teile[e.schluessel])}
          </b>
          <small className="text-[10px] font-semibold sm:text-[11px]">
            <span className="sm:hidden">{e.kurz}</span>
            <span className="hidden sm:inline">{teile[e.schluessel] === 1 ? e.eins : e.viele}</span>
          </small>
        </span>
      ))}
    </span>
  );
}

/** Als Kacheln für Landingpage und Pop-up (Klassen in `webseite.css`). Nach dem Beginn nichts. */
export function CountdownKacheln({ beginn, titel = "Noch bis zum Start" }: { beginn: string; titel?: string }) {
  const jetzt = useSekundentakt();
  const teile = countdownTeile(beginn, jetzt);
  if (!teile) return null;
  return (
    <div className="iv-countdown">
      <span className="iv-countdown-titel">{titel}</span>
      <span className="sr-only">{vorlesen(teile)}</span>
      <div className="iv-countdown-kacheln" aria-hidden="true">
        {EINHEITEN.map((e) => (
          <span key={e.schluessel} className="iv-countdown-kachel">
            <b>{e.schluessel === "tage" ? teile.tage : zweistellig(teile[e.schluessel])}</b>
            <small>{e.schluessel === "tage" && teile.tage === 1 ? "Tag" : e.lang}</small>
          </span>
        ))}
      </div>
    </div>
  );
}
